import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";

import { Form } from "@/components/ui/form";
import { useEditForm } from "@/features/Edit/hooks";
import { FormActionsUpdate } from "@/features/FormCard/Actions";
import { Diff } from "@/features/FormCard/Diff";
import {
  AutocompleteAsyncField,
  InputField,
  SelectField,
  TextareaField,
} from "@/features/FormCard/Fields";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";
import { preprocessTextAsyncAutocompleteField } from "@/features/FormCard/utils";

import { contactTypeOptions } from "./filter";

const FormSchema = z.object({
  client: z.string().min(1, "Client is required"),
  sub_client: z.string().min(1, "Sub-client is required"),
  company_name: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string(),
        z.object({
          id: z.number(),
          name: z.string(),
          text: z.string(),
        }),
      ])
      .optional(),
  ),
  company_id: z.coerce.number().min(1, "Company is required"),
  contact: z.string().trim().min(1, "Contact is required"),
  contact_type: z.string().min(1, "Contact type is required"),
  description: z.string().nullable().optional(),
});

export function FoBankContactEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
  } = useEditForm({
    disable_translations_request: true,
    id,
    client,
    table_name,
    FormSchema,
    adjustOnReset: (key, value, fetchedData) => {
      if (key === "company_name") {
        return {
          id: fetchedData.company_id,
          name: value,
          text: value,
        };
      }
      return value;
    },
  });

  return (
    <div className="grid place-items-center h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                Contact bancaire {id}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="client"
                    label="Client"
                    isRequired
                    capitalize
                    control={form.control}
                  />
                  <InputField
                    name="sub_client"
                    label="Sub-client"
                    isRequired
                    capitalize
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteAsyncField
                    name="company_name"
                    label="Banque"
                    base_url="instit-quantilia/companies"
                    column_name="name"
                    id_column="company_id"
                    isRequired
                    control={form.control}
                  />
                  <InputField
                    name="contact"
                    label="Contact (phone / email)"
                    isRequired
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="contact_type"
                    label="Type de contact"
                    options={contactTypeOptions}
                    control={form.control}
                    isRequired
                  />
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <TextareaField
                    name="description"
                    label="Description"
                    control={form.control}
                    placeholder={t("form.placeholders.description")}
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                />
              </CardFooter>
            </form>
          </Form>

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
          >
            {changedFields
              ? Diff(changedFields, ["company_id"])
              : "No changes detected"}
          </ConfirmationModal>

          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
      </div>
    </div>
  );
}
